import { useDispatch } from 'react-redux';

import { NsCard } from 'components/common/ns-card/NsCard';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { useNavigation } from 'hooks/useNavigation';
import { useEligibleFuel } from 'hooks/useEligibleFuel';
import { setPersonaliseQuoteShowFuel } from 'state/settings/settings-slice';
import { setElectric, setGas } from 'state/fuel-type/fuel-type-slice';
import { FUEL_OPTIONS } from 'config/app-config';
import { anyTrue, capitalize } from 'utils/misc';
import { TrackedCta } from 'components/direct-sales/select-options/components/TrackedCta';
import { setValidationRequired } from 'state/meterpoint-validation/meterpoint-validation-slice';
import { useHasMixedMeters } from 'hooks/useHasMixedMeters';

import type { NsCardProps } from 'components/common/ns-card/NsCard';

type DataMapping = {
  gas?: boolean;
  electricity?: boolean;
  fuelType?: string;
  fuelName?: string;
  decoration?: NsCardProps['decoration'];
};

const dataMapping: DataMapping[] = [
  {
    gas: true,
    electricity: false,
    fuelType: FUEL_OPTIONS.GAS,
    fuelName: capitalize(FUEL_OPTIONS.GAS),
    decoration: 'gas'
  },
  {
    gas: false,
    electricity: true,
    fuelType: FUEL_OPTIONS.ELECTRIC,
    fuelName: `${capitalize(FUEL_OPTIONS.ELECTRIC)}`,
    decoration: 'bulb'
  }
];

const getData = ({ gas, electricity }) => {
  return (
    dataMapping.find(
      (data) => data.gas === gas && data.electricity === electricity
    ) || {}
  );
};

type Props = {
  name: string;
};

export const EligibleFuelCard = ({ name }: Props) => {
  const dispatch = useDispatch();
  const eligibleFuels = useEligibleFuel();
  const hasMixedMeters = useHasMixedMeters();
  const { eventTypes, next } = useNavigation();
  const shouldShow = anyTrue(eligibleFuels) && !hasMixedMeters;

  const { decoration, fuelName, fuelType } = getData({
    gas: eligibleFuels[FUEL_OPTIONS.GAS],
    electricity: eligibleFuels[FUEL_OPTIONS.ELECTRIC]
  });
  const ctaFuelText = eligibleFuels[FUEL_OPTIONS.ELECTRIC]
    ? `an ${fuelName}`
    : `a ${fuelName}`;

  const handleGoNext = () => {
    next({ eventType: eventTypes.ELIGIBLE_FUEL });

    eligibleFuels[FUEL_OPTIONS.GAS] && dispatch(setGas());
    eligibleFuels[FUEL_OPTIONS.ELECTRIC] && dispatch(setElectric());
    dispatch(setPersonaliseQuoteShowFuel(false));
    dispatch(
      setValidationRequired({
        electricity: false,
        gas: false
      })
    );
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <NsCard
      decoration={decoration}
      heading={`Get a quote for your ${fuelName}`}
      testId="eligibleFuelCardTestId"
      action={
        <TrackedCta name={name}>
          <NsPrimaryCta onClick={handleGoNext}>
            Get {ctaFuelText} quote
          </NsPrimaryCta>
        </TrackedCta>
      }
    >
      <p>You&apos;ll find our cheapest {fuelType} tariffs right here.</p>
    </NsCard>
  );
};

export default EligibleFuelCard;
